import type {
  ChangesInbox,
  Declassification,
  ProjectView,
  WikiChangeItemRef,
  WikiChangeSet,
  WikiClosedChangeSets,
  WikiExport,
  WikiIndex,
  WikiPageView,
  WikiReviewQueue,
  WikiVersionCompare,
} from "@esi/shared";
import { CSRF_HEADER, changesInbox, WIKI_ACTIVITY_CURSOR_HEADER } from "@esi/shared";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "../api.ts";
import { noContent, ok } from "../api-errors.ts";
import { canChange } from "../projects/capabilities.ts";
import { useSession } from "../session.tsx";

export const wikiKeys = {
  all: (projectId: string) => ["projects", projectId, "wiki"] as const,
  index: (projectId: string) => ["projects", projectId, "wiki", "index"] as const,
  page: (projectId: string, pageId: string, version: number | undefined) =>
    ["projects", projectId, "wiki", "page", pageId, version ?? "current"] as const,
  review: (projectId: string) => ["projects", projectId, "wiki", "review"] as const,
  declassifications: (projectId: string) =>
    ["projects", projectId, "wiki", "declassifications"] as const,
  export: (projectId: string, exportId: string) =>
    ["projects", projectId, "wiki", "export", exportId] as const,
};

export function useWikiIndex(projectId: string, enabled = true) {
  return useQuery({
    queryKey: wikiKeys.index(projectId),
    enabled,
    queryFn: async (): Promise<WikiIndex> =>
      ok(await api.api.projects[":projectId"].wiki.$get({ param: { projectId } })),
    refetchInterval: (query) =>
      query.state.data?.pages.some((p) => p.status === "processing") ? 3000 : false,
  });
}

export function useWikiPage(
  projectId: string,
  pageId: string,
  version: number | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: wikiKeys.page(projectId, pageId, version),
    enabled,
    queryFn: async (): Promise<WikiPageView> =>
      ok(
        await api.api.projects[":projectId"].wiki.pages[":pageId"].$get({
          param: { projectId, pageId },
          query: version ? { version: String(version) } : {},
        }),
      ),
  });
}

/**
 * The change set holding a legacy draft link's item (a draft ID is its item ID). A miss the
 * server explains is a not-found; a bare route 404 is the previous API during a canary.
 */
export function useResolveChangeItem(projectId: string, itemId: string) {
  return useQuery({
    queryKey: [...wikiKeys.all(projectId), "change-item", itemId] as const,
    retry: false,
    queryFn: async (): Promise<WikiChangeItemRef> =>
      ok(
        await api.api.projects[":projectId"].wiki["change-items"][":itemId"].$get({
          param: { projectId, itemId },
        }),
      ),
  });
}

/** The review queue; `sizes` sizes each edit (the Changes inbox, not the count). */
export function useReviewQueue(projectId: string, enabled = true, sizes = false) {
  return useQuery({
    queryKey: [...wikiKeys.review(projectId), sizes ? "sized" : "plain"],
    enabled,
    queryFn: async (): Promise<WikiReviewQueue> =>
      ok(
        await api.api.projects[":projectId"].wiki.review.$get({
          param: { projectId },
          query: sizes ? { sizes: "1" } : {},
        }),
      ),
  });
}

/**
 * The review queue as the Changes inbox (wiki-collaboration-ux D4, spec `project-wiki` "Review
 * count counts only rows the viewer must act on"): `inbox.count` is the review count wherever it
 * shows, and the inbox's To decide lists the same rows.
 */
export function useChangesInbox(
  project: Pick<ProjectView, "id" | "capabilities" | "frozenAt">,
  enabled = true,
  /** Size each edit: the inbox's rows, never the count alone. */
  sizes = false,
): { queue: ReturnType<typeof useReviewQueue>; inbox: ChangesInbox | null } {
  const queue = useReviewQueue(project.id, enabled, sizes);
  const { userId } = useSession();
  const { capabilities, frozenAt } = project;
  const inbox = useMemo(() => {
    const viewer = { capabilities, frozenAt };
    return queue.data
      ? changesInbox(queue.data, {
          userId,
          canPublish: canChange(viewer, "wiki.publish"),
          canResolve: (kind) =>
            canChange(viewer, kind === "redeclassify" ? "declassify" : "wiki.resolve_review"),
        })
      : null;
  }, [queue.data, userId, capabilities, frozenAt]);
  return { queue, inbox };
}

export function useDeclassifications(projectId: string, enabled: boolean) {
  return useQuery({
    queryKey: wikiKeys.declassifications(projectId),
    enabled,
    queryFn: async (): Promise<Declassification[]> =>
      ok(await api.api.projects[":projectId"].declassifications.$get({ param: { projectId } })),
  });
}

/** Polls a requested export until it is built. */
export function useWikiExport(projectId: string, exportId: string | null) {
  return useQuery({
    queryKey: wikiKeys.export(projectId, exportId ?? ""),
    enabled: exportId !== null,
    queryFn: async (): Promise<WikiExport> =>
      ok(
        await api.api.projects[":projectId"].wiki.exports[":exportId"].$get({
          param: { projectId, exportId: exportId ?? "" },
        }),
      ),
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 2000 : false),
  });
}

/** Slice-1 endpoints; no new API is required during the canary. */
export function useChangeSet(projectId: string, id: string, enabled = true) {
  return useQuery({
    queryKey: [...wikiKeys.all(projectId), "change", id],
    enabled,
    retry: false,
    queryFn: async () =>
      ok(
        await api.api.projects[":projectId"].wiki["change-sets"][":id"].$get({
          param: { projectId, id },
        }),
      ),
  });
}
export function useChangeItem(projectId: string, id: string, itemId: string | undefined) {
  return useQuery({
    queryKey: [...wikiKeys.all(projectId), "change", id, "item", itemId],
    enabled: !!itemId,
    retry: false,
    queryFn: async () =>
      ok(
        await api.api.projects[":projectId"].wiki["change-sets"][":id"].items[":itemId"].$get({
          param: { projectId, id, itemId: itemId ?? "" },
        }),
      ),
  });
}
export function useWikiVersions(projectId: string, pageId: string) {
  return useQuery({
    queryKey: [...wikiKeys.all(projectId), "versions", pageId],
    queryFn: async () =>
      ok(
        await api.api.projects[":projectId"].wiki.pages[":pageId"].versions.$get({
          param: { projectId, pageId },
        }),
      ),
  });
}

/**
 * The Changes inbox's Closed view (wiki-collaboration-ux D5): published and discarded sets,
 * newest decision first, 50 a page, searched by title. A missing route (an N-1 API during the
 * canary) reads as a plain error.
 */
export function useClosedChanges(projectId: string, query: string, enabled = true) {
  const q = query.trim();
  return useInfiniteQuery({
    queryKey: [...wikiKeys.all(projectId), "closed", q],
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<WikiClosedChangeSets> =>
      ok(
        await api.api.projects[":projectId"].wiki["closed-changes"].$get({
          param: { projectId },
          query: { ...(q && { q }), ...(pageParam && { cursor: pageParam }) },
        }),
      ),
    getNextPageParam: (page) => page.next ?? undefined,
  });
}

export function useWikiActivity(projectId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...wikiKeys.all(projectId), "activity"],
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const response = await api.api.projects[":projectId"].wiki.activity.$get({
        param: { projectId },
        query: { limit: "50", ...(pageParam ? { before: pageParam } : {}) },
      });
      const items = await ok(response);
      // N-1 sends only the array. New servers also expose a cursor after a capped hidden scan.
      const cursor = response.headers.get(WIKI_ACTIVITY_CURSOR_HEADER);
      return {
        items,
        nextBefore:
          cursor ??
          (items.length && items.at(-1)?.updatedAt !== pageParam
            ? items.at(-1)?.updatedAt
            : undefined),
      };
    },
    getNextPageParam: (page) => page.nextBefore ?? undefined,
  });
}

/** Two versions of a page, oldest first, with their own title, type and Markdown. */
export function useWikiVersionCompare(projectId: string, pageId: string, from: number, to: number) {
  return useQuery({
    queryKey: [...wikiKeys.all(projectId), "compare", pageId, from, to],
    retry: false,
    queryFn: async (): Promise<WikiVersionCompare> =>
      ok(
        await api.api.projects[":projectId"].wiki.pages[":pageId"].versions.compare.$get({
          param: { projectId, pageId },
          query: { from: String(from), to: String(to) },
        }),
      ),
  });
}

/** Restore one version of a page, or revert a published change set: a new change set for review. */
export function useRevert(projectId: string) {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      target: { pageId: string; version: number } | { changeSetId: string },
    ): Promise<WikiChangeSet> => {
      const headers = { [CSRF_HEADER]: csrfToken };
      if ("changeSetId" in target) {
        return ok(
          await api.api.projects[":projectId"].wiki["change-sets"][":id"].revert.$post(
            { param: { projectId, id: target.changeSetId } },
            { headers },
          ),
        );
      }
      return ok(
        await api.api.projects[":projectId"].wiki.pages[":pageId"].versions[":v"].revert.$post(
          { param: { projectId, pageId: target.pageId, v: String(target.version) } },
          { headers },
        ),
      );
    },
    // The review queue, activity and versions all live under the wiki's key.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: wikiKeys.all(projectId) }),
  });
}

/**
 * A page's owner and its verification (wiki-okf-structure D8, D9): `owner` names or clears the
 * owner, `verify` attests the shown current version, `withdraw` removes the verification. None
 * writes a version; each refreshes the page.
 */
export function usePageMetadata(projectId: string, pageId: string) {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const param = { projectId, pageId };
  const headers = { [CSRF_HEADER]: csrfToken };
  const page = api.api.projects[":projectId"].wiki.pages[":pageId"];
  return useMutation({
    mutationFn: async (
      change:
        | { kind: "owner"; userId: string | null }
        | { kind: "verify"; version: number }
        | { kind: "withdraw" },
    ): Promise<void> => {
      if (change.kind === "owner") {
        return noContent(
          await page.owner.$put({ param, json: { userId: change.userId } }, { headers }),
        );
      }
      if (change.kind === "verify") {
        return noContent(
          await page.verification.$post({ param, json: { version: change.version } }, { headers }),
        );
      }
      return noContent(await page.verification.$delete({ param }, { headers }));
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [...wikiKeys.all(projectId), "page", pageId] }),
  });
}
